import { useState } from 'react';
import TextField from '@mui/material/TextField';
import { Button, Alert, MenuItem } from '@mui/material';
import { ExtendedFormik, Form } from '../extended-formik';
import { OutlinedTextField } from '../shared';
import * as Yup from 'yup';
import styled from 'styled-components';

import { useAddUserMutation } from '../../redux';
import { useAuthContext } from '../../context/auth-context';
import { useZoneCatalog } from '../../hooks/use-zone-catalog';
import { ROLES, ROLES_REQUIRING_ZONE, assignableRoles, roleLabel } from '../../constants/roles';
import usernameUtils from '../../utils/usernameUtils';

const Root = styled.div`
  margin-top: 2%;
`;

const Label = styled.label`
  display: flex;
  flex: wrap;
  margin-bottom: 1%;
  font-size: 16px;
  font-weight: 600;
`;

const Row = styled.div`
  margin-top: 1%;
`;

const Error = styled.div`
  display: flex;
  flex: wrap;
  margin-bottom: 1%;
  font-size: 16px;
  color: red;
`;

export const CreateUserForm = ({ handleClose }) => {
  const { activeCamp, hasPermission } = useAuthContext();
  const [createUser] = useAddUserMutation();
  const [serverError, setServerError] = useState('');
  // Acotado al campamento activo, igual que el diálogo de cambio de rol: el usuario
  // nuevo se crea para este campamento (`camp: activeCamp?._id` va en el submit).
  const { zones, isFetching: loadingZones } = useZoneCatalog({ campId: activeCamp?._id });
  const roleOptions = assignableRoles(hasPermission);

  return (
    <Root>
      <ExtendedFormik
        initialValues={{
          fullname: '',
          username: '',
          email: '',
          password: '',
          confirmationPassword: '',
          role: ROLES.BASIC,
          zone: '',
        }}
        validationSchema={Yup.object({
          fullname:             Yup.string().required('Requerido'),
          username:             Yup.string()
            .trim()
            .lowercase()
            .min(3, 'Mínimo 3 caracteres')
            .max(30, 'Máximo 30 caracteres')
            .matches(
              usernameUtils.regex,
              'Solo minúsculas, números, punto, guion y guion bajo; no iniciar/terminar con separadores'
            )
            .required('Requerido'),
          email:                Yup.string().email('Email inválido').optional(),
          password:             Yup.string().min(6, 'Mínimo 6 caracteres').required('Requerido'),
          confirmationPassword: Yup.string()
            .oneOf([Yup.ref('password')], 'Las contraseñas no coinciden')
            .required('Requerido'),
          role:                 Yup.string().oneOf(roleOptions, 'Rol no válido').required('Requerido'),
          // El servidor rechaza un `sec` sin zona; validarlo aquí evita el viaje.
          zone:                 Yup.string().when('role', (role, schema) =>
            ROLES_REQUIRING_ZONE.includes(role)
              ? schema.required('El Secretario de Zona necesita una zona')
              : schema,
          ),
        })}
        onSubmit={async (values) => {
          setServerError('');
          const normalizedUsername = usernameUtils.normalize(values.username);
          const result = await createUser({
            username: normalizedUsername,
            password: values.password,
            fullname: values.fullname,
            camp:     activeCamp?._id,
            role:     values.role,
            ...(ROLES_REQUIRING_ZONE.includes(values.role) ? { zone: values.zone } : {}),
            ...(values.email ? { email: values.email } : {}),
          });
          if (result.error) {
            setServerError(result.error.data?.message || 'Error al crear el usuario');
          } else {
            handleClose();
          }
        }}
      >
        {({ values, errors, handleChange }) => (
          <Form>
            {serverError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setServerError('')}>
                {serverError}
              </Alert>
            )}
            <Row>
              <Label>Nombre Completo</Label>
              <TextField
                id='fullname' label='Requerido'
                value={values.fullname} onChange={handleChange}
                fullWidth required
              />
              {errors.fullname && <Error>{errors.fullname}</Error>}
            </Row>

            <Row>
              <Label>Usuario</Label>
              <TextField
                id='username' label='Requerido'
                value={values.username}
                onChange={e => handleChange({
                  target: {
                    name: 'username',
                    value: usernameUtils.sanitizeInput(e.target.value),
                  },
                })}
                inputProps={{
                  minLength: 3,
                  maxLength: 30,
                  pattern: usernameUtils.regex.source,
                  autoCapitalize: 'none',
                }}
                fullWidth required
              />
              {errors.username && <Error>{errors.username}</Error>}
            </Row>

            <Row>
              <Label>Email</Label>
              <TextField
                id='email' label='Opcional' type='email'
                value={values.email} onChange={handleChange}
                fullWidth
              />
              {errors.email && <Error>{errors.email}</Error>}
            </Row>

            <Row>
              <Label>Rol</Label>
              <TextField
                select
                id='role' name='role'
                value={values.role} onChange={handleChange}
                fullWidth required
              >
                {roleOptions.map(role => (
                  <MenuItem key={role} value={role}>{roleLabel(role)}</MenuItem>
                ))}
              </TextField>
              {errors.role && <Error>{errors.role}</Error>}
            </Row>

            {/* La zona solo aparece cuando el rol la exige: para un juez no significa nada. */}
            {ROLES_REQUIRING_ZONE.includes(values.role) && (
              <Row>
                <Label>Zona</Label>
                <TextField
                  select
                  id='zone' name='zone'
                  value={zones.includes(values.zone) ? values.zone : ''}
                  onChange={handleChange}
                  helperText={
                    loadingZones
                      ? 'Cargando zonas…'
                      : zones.length === 0
                        ? 'Este campamento no tiene zonas. Créalas en Configuración.'
                        : 'Define qué clubes y reportes verá.'
                  }
                  disabled={loadingZones}
                  fullWidth required
                >
                  {zones.map(zone => (
                    <MenuItem key={zone} value={zone}>{zone}</MenuItem>
                  ))}
                </TextField>
                {errors.zone && <Error>{errors.zone}</Error>}
              </Row>
            )}

            <Row>
              <Label>Contraseña</Label>
              <OutlinedTextField
                id='password' label='Requerido'
                value={values.password} onChange={handleChange}
                isPassword autoComplete='off' fullWidth required
              />
              {errors.password && <Error>{errors.password}</Error>}
            </Row>

            <Row>
              <Label>Confirmación de Contraseña</Label>
              <OutlinedTextField
                id='confirmationPassword' label='Requerido'
                value={values.confirmationPassword} onChange={handleChange}
                isPassword autoComplete='off' fullWidth required
              />
              {errors.confirmationPassword && <Error>{errors.confirmationPassword}</Error>}
            </Row>

            <Row>
              <Button sx={{ float: 'right' }} type='submit'>
                Guardar
              </Button>
              <Button
                sx={{ float: 'right', color: 'red' }}
                onClick={e => { e.preventDefault(); handleClose(); }}
              >
                Cancelar
              </Button>
            </Row>
          </Form>
        )}
      </ExtendedFormik>
    </Root>
  );
};
